import { DataTableColumn, OrderData, TableRow } from './models';
import styles from './DataTable.module.scss';
import { FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { IoSwapVertical } from 'react-icons/io5';
import useSticky from 'common/hooks/useSticky';
import { isTablet } from 'Config';
import ColumnResizer from 'react-table-column-resizer';

const getCellWidthStyle = <TRow extends TableRow>(col: DataTableColumn<TRow>) => {
    return {
        width: col.width ?? undefined,
        minWidth: col.minWidth ?? undefined,
    };
};

const getColumnCellStyle = <TRow extends TableRow>(col: DataTableColumn<TRow>) => {
    return {
        textAlign: col.columnCellAlignment || 'left',
        ...getCellWidthStyle(col),
    };
};

interface DefaultColumnProps<TRow> {
    col: DataTableColumn<TRow>;
    order?: OrderData<TRow> | null;
    colIndex?: number;
    disableResize?: boolean;
    onClickOrder: () => void;
    onOrder?: (orderData: OrderData<TRow>) => void;
    onColResize?: (width: number) => void;
}

export const DefaultColumn = <TRow extends TableRow>({
    col,
    colIndex,
    order,
    disableResize = false,
    onClickOrder,
    onOrder,
    onColResize
}: DefaultColumnProps<TRow>) => {
    const { ref, stickyData } = useSticky<HTMLTableCellElement>();

    const renderActiveFilterIcon = () => {
        return (
            <div>
                {order && !order.isOrderAsc && <FaSortAmountUp className={`${styles.headerSortIcon} ${styles.active}`} />}
                {order && order.isOrderAsc && <FaSortAmountDown className={`${styles.headerSortIcon} ${styles.active}`} />}
            </div>
        );
    };

    const renderHasFilterIcon = () => {
        return (
            <div>
                {onOrder && <IoSwapVertical className={`${styles.headerSortIcon}`} />}
            </div>
        );
    };

    const getHideOnClass = () => {
        if (!col.hideOn) return '';
        if (col.hideOn.includes('xxxl')) return styles.xxxl;
        if (col.hideOn.includes('xxl')) return styles.xxl;
        if (col.hideOn.includes('xl')) return styles.xl;
        if (col.hideOn.includes('lg')) return styles.lg;
        if (col.hideOn.includes('md')) return styles.md;
        if (col.hideOn.includes('sm')) return styles.sm;
        return '';
    };

    return (
        <>
            <th
                ref={ref}
                className={`
                    ${getHideOnClass()}
                    ${styles.columnHeader}
                    ${col.columnCellClassName || ''}
                    ${stickyData.topIsSticky ? styles.isSticky : ''}
                `}
                style={getColumnCellStyle(col)}
            >
                <div
                    onClick={() => col.hideOrderButton ? null : onClickOrder()}
                    className={`
                        ${styles.headerInfo}
                        ${col.columnCellAlignment === 'right' ? styles.headerInfoRight : ''}
                        ${onOrder && !col.hideOrderButton ? styles.clickable : ''}
                    `}
                >
                    {!col.hideOrderButton && (order && order.field === col.field ? renderActiveFilterIcon() : renderHasFilterIcon())}
                    {col.name}
                </div>
            </th>
            {!disableResize && <ColumnResizer
                id={colIndex ?? 0}
                className={`${styles.columnResizer} ${stickyData.topIsSticky ? styles.isSticky : ''}`}
                defaultWidth={col.width}
                minWidth={50}
                maxWidth={null}
                disabled={false}
                resizeStart={() => { /* noop */ }}
                resizeEnd={(width: number) => onColResize && onColResize(width)}
            />}
        </>
    );
};

interface ActionColumnHeaderProps {
    onRenderAction?: (row: any) => JSX.Element | undefined;
}

export const ActionColumnHeader = ({ onRenderAction }: ActionColumnHeaderProps) => {
    const { ref, stickyData } = useSticky<HTMLTableCellElement>();

    if (!onRenderAction) {
        return null;
    }

    return (
        <th
            ref={ref}
            className={`
                ${styles.columnHeader}
                ${styles.actionColumnHeader}
                ${stickyData.topIsSticky ? styles.isSticky : ''}
                ${stickyData.rightIsSticky ? styles.rightIsSticky : ''}
            `}
        >
            <div className={styles.headerInfo}>&nbsp;</div>
        </th>
    );
};

interface ActionColumnCellProps<TRow> {
    row: TRow;
    rowIndex: number;
    striped: boolean;
    onRenderAction?: (row: TRow) => JSX.Element | undefined;
    style?: React.CSSProperties;
}

export const ActionColumnCell = <TRow extends TableRow>({
    row,
    rowIndex,
    striped,
    onRenderAction,
    style
}: ActionColumnCellProps<TRow>) => {
    const { ref, stickyData } = useSticky<HTMLTableCellElement>();
    const isMobile = isTablet(window.innerWidth);

    if (!onRenderAction) {
        return null;
    }

    return (
        <td
            ref={ref}
            className={`
                ${styles.column}
                ${striped && rowIndex % 2 === 0 ? styles.striped : ''}
                ${isMobile ? styles.mobile : ''}
                ${styles.actionColumn}
                ${stickyData.rightIsSticky ? styles.rightIsSticky : ''}
                global-action-column
            `}
            style={style}
        >
            {onRenderAction(row)}
        </td>
    );
};
